import React, { useEffect, useState } from 'react';
import { ListBoxDragEvent, ListBoxItemClickEvent, processListBoxDragAndDrop } from '@progress/kendo-react-listbox';
import DragDropLists from '@components-clean/core/drag-drop-lists-core/drag-drop-lists/index';
import {
    filterDragDropColumn,
    highlightDroppedItems,
    parseDragDropItems,
    sortDragDropItems,
    updateDragDropClickedItem,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.helper';
import {
    DragDropColumnsState,
    DragDropListItemProps,
    DragDropListsCoreProps,
    ProcessDragDropListResult,
    Side,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import SingleDragDropList from '@components-clean/core/drag-drop-lists-core/single-drag-drop-list/indext';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';

const DragDropListsCore = ({
    list1Items,
    list2Items,
    isTwoLists,
    onChangeItemsLeft,
    onChangeItemsRight,
    list1Header,
    list2Header,
    disabled,
    className,
    idField,
    renderItemLeft,
    renderItemRight,
    filterItem,
    list1HeaderActions,
    hideTransferAllButtons,
    disableFilterLeft = true,
    disableFilterRight,
    disableReorder,
    sortLefCol,
    optionalItemClickCallback,
    handleReset,
    disableRightColSort,
}: DragDropListsCoreProps) => {
    // NOTE #0001: It might be confusing a little because only the right ListBox column can have a filter for the DragDropLists component.
    // We have to populate the rightColumn for the SingleDragDropList component since it has a filter too
    const [columnsState, setColumnsState] = useState<DragDropColumnsState>(() => ({
        leftColumn: list2Items ? parseDragDropItems(list1Items) : [],
        rightColumn: list2Items ? sortDragDropItems(parseDragDropItems(list2Items)) : parseDragDropItems(list1Items),
    }));
    const { readOnly } = useReadOnlyContext();
    const disabledOrReadonly = disabled || readOnly;

    const [filterLeft, setFilterLeft] = useState<string>('');
    const [filterRight, setFilterRight] = useState<string>('');
    const [draggedItem, setDraggedItem] = useState<DragDropListItemProps | null>(null);

    const handleItemClick = (event: ListBoxItemClickEvent, data: Side, connectedData: Side) => {
        setColumnsState(updateDragDropClickedItem(columnsState, event, data, connectedData, idField));
        if (optionalItemClickCallback) {
            optionalItemClickCallback(event);
        }
    };

    const handleDragStart = (event: ListBoxDragEvent) => {
        if (disabledOrReadonly) {
            return;
        }

        setDraggedItem(event.dataItem as DragDropListItemProps);
    };

    const clearFilter = () => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (filterLeft) {
            setFilterLeft('');
        }
        if (filterRight) {
            setFilterRight('');
        }
        /* eslint-enable @eslint-react/set-state-in-effect */
    };

    const isRightToLeftColumnDrop = (event: ListBoxDragEvent) => {
        const isDifferentTargetCol =
            event.target.props.data.findIndex(
                (item: DragDropListItemProps) => item[idField] === draggedItem?.[idField],
            ) === -1;

        if (isDifferentTargetCol) {
            // Check if it is right column item
            return columnsState.rightColumn.findIndex((item) => item[idField] === draggedItem?.[idField]) !== -1;
        }

        return false;
    };

    const isLeftToRightColumnDrop = (event: ListBoxDragEvent) => {
        const isDifferentTargetCol =
            event.target.props.data.findIndex(
                (item: DragDropListItemProps) => item[idField] === draggedItem?.[idField],
            ) === -1;

        if (isDifferentTargetCol) {
            // Check if it is left column item
            return columnsState.leftColumn.findIndex((item) => item[idField] === draggedItem?.[idField]) !== -1;
        }

        return false;
    };

    const handleDrop = (event: ListBoxDragEvent) => {
        if (disabledOrReadonly) {
            return;
        }

        const draggedItems = draggedItem?.selected
            ? [
                  ...columnsState.leftColumn.filter((item: DragDropListItemProps) => item.selected),
                  ...columnsState.rightColumn.filter((item: DragDropListItemProps) => item.selected),
              ]
            : [draggedItem!];

        const isRightToLeft = isRightToLeftColumnDrop(event);
        const isLeftToRight = isLeftToRightColumnDrop(event);

        let anchor = event.dataItem;
        let result: ProcessDragDropListResult;
        draggedItems.forEach((item: DragDropListItemProps) => {
            result = processListBoxDragAndDrop(
                result ? result.listBoxOneData : columnsState.leftColumn,
                result ? result.listBoxTwoData : columnsState.rightColumn,
                item,
                anchor,
                idField,
            ) as ProcessDragDropListResult;
            anchor = item;
        });

        if (draggedItems.length > 0) {
            result = highlightDroppedItems(result!, draggedItems, idField);

            if (onChangeItemsLeft) onChangeItemsLeft(result!.listBoxOneData);
            if (onChangeItemsRight) onChangeItemsRight(result!.listBoxTwoData);

            setColumnsState({
                ...columnsState,
                leftColumn: filterDragDropColumn(
                    isRightToLeft && sortLefCol ? sortDragDropItems(result!.listBoxOneData) : result!.listBoxOneData,
                    filterLeft,
                    idField,
                    filterItem,
                ),
                rightColumn: filterDragDropColumn(
                    isLeftToRight ? sortDragDropItems(result!.listBoxTwoData) : result!.listBoxTwoData,
                    filterRight,
                    idField,
                    filterItem,
                ),
            });

            // Blur active element to remove focus
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }
    };

    // NOTE: see the "NOTE #0001" {@link}
    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setColumnsState({
            leftColumn: list2Items ? (parseDragDropItems(list1Items) as DragDropListItemProps[]) : [],
            rightColumn: filterDragDropColumn(
                list2Items
                    ? disableRightColSort
                        ? (parseDragDropItems(list2Items) as DragDropListItemProps[])
                        : sortDragDropItems(parseDragDropItems(list2Items) as DragDropListItemProps[])
                    : (parseDragDropItems(list1Items) as DragDropListItemProps[]),
                filterRight,
                idField,
                filterItem,
            ),
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [list1Items, list2Items]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setColumnsState({
            ...columnsState,
            leftColumn: filterDragDropColumn(
                // Reset sorting of the list when it became inactive
                disabled && isTwoLists ? sortDragDropItems(columnsState.leftColumn) : columnsState.leftColumn,
                filterLeft,
                idField,
                filterItem,
            ),
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [filterLeft]);
    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setColumnsState({
            ...columnsState,
            rightColumn: filterDragDropColumn(
                // Reset sorting of the list when it became inactive
                disabled && isTwoLists ? sortDragDropItems(columnsState.rightColumn) : columnsState.rightColumn,
                filterRight,
                idField,
                filterItem,
            ),
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [filterRight]);

    useEffect(() => {
        if (disabledOrReadonly) {
            clearFilter();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [disabledOrReadonly]);

    return isTwoLists ? (
        <DragDropLists
            columnsState={columnsState}
            setColumnsState={setColumnsState}
            onChangeItemsLeft={onChangeItemsLeft}
            onChangeItemsRight={onChangeItemsRight}
            handleDragStart={handleDragStart}
            handleDrop={handleDrop}
            handleItemClick={handleItemClick}
            filterLeft={disableFilterLeft ? undefined : { value: filterLeft, setValue: setFilterLeft }}
            filterRight={disableFilterRight ? undefined : { value: filterRight, setValue: setFilterRight }}
            idField={idField}
            list1Header={list1Header}
            list2Header={list2Header ?? ''}
            renderItemLeft={renderItemLeft}
            renderItemRight={renderItemRight}
            className={className}
            disabled={disabled}
            list1HeaderActions={list1HeaderActions}
            hideTransferAllButtons={hideTransferAllButtons}
            disableReorder={disableReorder}
            sortLefCol={sortLefCol}
            handleReset={handleReset}
            disableRightColSort
        />
    ) : (
        <SingleDragDropList
            columnsState={columnsState}
            handleDragStart={handleDragStart}
            handleDrop={handleDrop}
            handleItemClick={handleItemClick}
            filter={filterRight}
            setFilter={setFilterRight}
            listHeader={list1Header}
            renderItemLeft={renderItemLeft}
            className={className}
            disabled={disabledOrReadonly}
        />
    );
};

export default DragDropListsCore;
